import React, { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaHome } from "react-icons/fa";
import { FaUsersGear } from "react-icons/fa6";
import { RiAdminFill } from "react-icons/ri";
import { HiUserGroup } from "react-icons/hi";
import { IoRestaurant } from "react-icons/io5";
import { BsFillInboxesFill } from "react-icons/bs";

import { Login } from "./pages/Login";
import { Home } from "./pages/Home";
import { Users } from "./pages/users/Index";
import UserInvoice from "./pages/users/invoice";
import { Admins } from "./pages/admin/Index";
import { AddAdmins } from "./pages/admin/Add";
import { Restaurants } from "./pages/restaurants/Index";
import { AddRestaurants } from "./pages/restaurants/Add";
import { EditRestaurants } from "./pages/restaurants/Edit";
import { Branches } from "./pages/restaurants/Branches";
import { Devices } from "./pages/restaurants/Devices";
import Privacy from "./pages/Privacy";
import { NotFound } from "./pages/NotFound";
import { Sidebar } from "./components/Sidebar";
import { Toolbar } from "./components/Toolbar";
import { authState } from "./recoil";
import { useRecoilValue } from "recoil";
import { ProtectedRoute, Role } from "./components/ProtectedRoute";
import RestaurantCashiers from "./pages/restaurantDashboard/Cashiers";
import { MdPointOfSale } from "react-icons/md";
import UserOrder from "./pages/users/order";
import RestaurantOrders from "./pages/restaurantDashboard/RestaurantUsers";
import { SpecterLanding } from "./pages/SpecterLanding";
import RestaurantsAdmins from "./pages/restaurantAdmin/Admins";
import { AddRestaurantAdmin } from "./pages/restaurantAdmin/Add";
import { Categories } from "./pages/categories/Index";
import { BiSolidCategory } from "react-icons/bi";
import { AddCategories } from "./pages/categories/Add";
import { EditCategories } from "./pages/categories/Edit";
import UserOrders from "./pages/restaurantDashboard/UserOrders";

export const App: React.FC = () => {
  const authToken = useRecoilValue(authState);

  const RedirectToApp: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
      (async () => {
        const response = await axios.post("/user/isVerify");
        if (response.data.verify && authToken.role === "restaurantAdmin") {
          navigate("/dashboard/cashiers");
        } else if (response.data.verify && authToken.role === "admin")
          navigate("/dashboard/home");
      })();
    }, [navigate]);
    return <Outlet />;
  };

  const VerifyToken = () => {
    const navigate = useNavigate();
    useEffect(() => {
      (async () => {
        const response = await axios.post("/user/isVerify");
        if (!response.data.verify) navigate("/dashboard/login");
      })();
    }, [navigate]);
    return <Outlet />;
  };

  return (
    <div>
      <Routes>
        <Route path="dashboard">
          <Route index element={<Navigate to="login" replace />} />
          <Route element={<RedirectToApp />}>
            <Route path="login" element={<Login />} />
          </Route>
          <Route element={<VerifyToken />}>
            <Route path="home" element={<Layout />}>
              <Route
                index
                element={
                  <ProtectedRoute
                    allowedRoles={["admin"]}
                    userRole={authToken.role as Role}
                  >
                    <Home />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["restaurantAdmin"]}
                  userRole={authToken.role as Role}
                >
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="cashiers" element={<RestaurantCashiers />} />
              <Route path="restaurant-orders" element={<RestaurantOrders />} />
              <Route
                path="restaurant-orders/user/:userId"
                element={<UserOrders />}
              />
            </Route>
            <Route
              path="admins"
              element={
                <ProtectedRoute
                  allowedRoles={["admin"]}
                  userRole={authToken.role as Role}
                >
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Admins />} />
              <Route path="add" element={<AddAdmins />} />
            </Route>
            <Route
              path="users"
              element={
                <ProtectedRoute
                  allowedRoles={["admin"]}
                  userRole={authToken.role as Role}
                >
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Users />} />
            </Route>
            <Route
              path="restaurants"
              element={
                <ProtectedRoute
                  allowedRoles={["admin"]}
                  userRole={authToken.role as Role}
                >
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Restaurants />} />
              <Route path="add" element={<AddRestaurants />} />
              <Route path="edit/:id" element={<EditRestaurants />} />
              <Route path="branches/:restId" element={<Branches />} />
              <Route path="devices/:restId/:branchId" element={<Devices />} />
            </Route>
            <Route
              path="restaurants-admins"
              element={
                <ProtectedRoute
                  allowedRoles={["admin"]}
                  userRole={authToken.role as Role}
                >
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<RestaurantsAdmins />} />
              <Route path="add" element={<AddRestaurantAdmin />} />
            </Route>
            <Route
              path="categories"
              element={
                <ProtectedRoute
                  allowedRoles={["admin"]}
                  userRole={authToken.role as Role}
                >
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Categories />} />
              <Route path="add" element={<AddCategories />} />
              <Route path="edit/:id" element={<EditCategories />} />
            </Route>
          </Route>
        </Route>
        <Route path="user">
          <Route path="invoice" element={<UserInvoice />} />
        </Route>
        <Route path="/" element={<SpecterLanding />} />
        <Route path="order/:id" element={<UserOrder />} />
        <Route path="Privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

const Layout: React.FC = () => {
  const authToken = useRecoilValue(authState);

  useEffect(() => {
    document.title = "Specter | Dashboard";
  }, []);

  if (!authToken?.token) return <Navigate to="/login" />;

  return (
    <div className="flex" dir="ltr">
      <Sidebar
        logo="/splash-logo.png"
        iconClass="text-2xl text-green-500"
        nameClass="text-green-600"
        list={[
          {
            link: "/dashboard/home",
            name: "Home",
            icon: <FaHome />,
            role: "admin",
          },
          {
            link: "/dashboard/admins",
            name: "Admin Users",
            icon: <RiAdminFill />,
            role: "admin",
          },
          {
            link: "/dashboard/users",
            name: "App Users",
            icon: <HiUserGroup />,
            role: "admin",
          },
          {
            link: "/dashboard/restaurants-admins",
            name: "Restaurants Admins",
            icon: <FaUsersGear />,
            role: "admin",
          },
          {
            link: "/dashboard/restaurants",
            name: "Restaurants",
            icon: <IoRestaurant />,
            role: "admin",
          },
          {
            link: "/dashboard/categories",
            name: "Categories",
            icon: <BiSolidCategory className="text-xl" />,
            role: "admin",
          },
          {
            link: "/dashboard/cashiers",
            name: "Cashiers",
            icon: <MdPointOfSale />,
            role: "restaurantAdmin",
          },
          {
            link: "/dashboard/restaurant-orders",
            name: "Orders",
            icon: <BsFillInboxesFill className="text-xl" />,
            role: "restaurantAdmin",
          },
        ]}
      />

      <div className="flex-1 px-5 overflow-x-auto h-screen overflow-y-auto">
        <Toolbar />
        <Outlet />
      </div>
    </div>
  );
};
